/**
 * CameraCapture – orchestrator. Composes hooks and presentational components.
 * Accepts optional notificationService for dependency injection.
 */
import React, { useRef, useState, useEffect, useCallback, useMemo, memo } from "react";
import PropTypes from "prop-types";
import Button from "../../components/ui/buttons/Button";
import GalleryIcon from "../../components/ui/icons/svgs/GalleryIcon";
import CameraPreview from "./CameraPreview";
import ImageEditor from "./ImageEditor";
import {
  OVERLAY_STYLE,
  HEADER_STYLE_FRAMED,
  HEADER_STYLE_FULLSCREEN,
  FOOTER_STYLE_FRAMED,
  FOOTER_STYLE_FULLSCREEN,
  CONTROLS_STYLE_BASE,
  TOUCH_TARGET_MIN,
  FOOTER_HEIGHT,
  FOOTER_ICON_SIZE,
  FLASH_MODES,
  ZOOM_PRESETS,
  STACK_STRIP_CSS,
  ALLOW_VIDEO_CAPTURE,
} from "./constants";
import {
  base64ArrayToFileArray,
  formatRecordingTime,
  getDefaultCameraFullScreen,
  estimateDominantAngleFromVideo,
  isAngleAlignedForOcr,
} from "./utils";
import { useRecording, useCameraDevices, useImageStack, useImageProcessing } from "./hooks";
import { VideoPreviewItem, StackStripOverlay } from "./components";

function CameraCapture({
  open = false,
  onClose,
  onSelect,
  inputFileType = "png,jpeg,jpg",
  multiple = false,
  maxSelect = null,
  title,
  note,
  maxFileSize = 50,
  allowVideo = ALLOW_VIDEO_CAPTURE,
  allowedFromGallery,
  centerBoxOverlay,
  centerBoxHint,
  centerBoxWidthRatio = 0.7,
  centerBoxHeightRatio = 0.5,
  notificationService,
}) {
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const reuploadInputRef = useRef(null);
  const captureTimerRef = useRef(null);

  const stack = useImageStack();
  const {
    imageFiles,
    setImageFiles,
    videoFiles,
    editedImage,
    setEditedImage,
    stackExpanded,
    setStackExpanded,
    stackClosing,
    closeStack,
    handleStackAnimationEnd,
    addImage,
    replaceImageAt,
    removeImage,
    removeVideo,
    addVideo,
    setEditedImageFromIndex,
    updateImageAtEditedIndex,
    handleEditorCancel,
    startRecapture,
    reset,
    replaceAtIndexRef,
    editedIndexRef,
  } = stack;

  const processing = useImageProcessing({
    inputFileType,
    maxFileSize,
    maxSelect,
    notificationService,
  });
  const {
    acceptAttr,
    validateGalleryFiles,
    processCapturedBuffer,
    processFileForReupload,
    processFilesForGallery,
    notifyCaptureFailed,
  } = processing;

  const onVideoComplete = useCallback((file) => addVideo(file), [addVideo]);
  const { isRecording, recordingElapsedMs, startRecording, stopRecording } = useRecording(
    videoRef,
    onVideoComplete
  );
  const {
    videoDevices,
    selectedDevice,
    facingMode,
    useFacingOnMobile,
    flashSupported,
    setFlashSupported,
    zoomRange,
    setZoomRange,
    handleFlipCamera,
    refreshDevices,
  } = useCameraDevices();

  const hasMultipleVideoIds =
    videoDevices.filter((d) => d.deviceId && d.deviceId !== "").length >= 2;

  const [openCamera, setOpenCamera] = useState(open);
  const [shouldCapture, setShouldCapture] = useState(false);
  const [captureMode, setCaptureMode] = useState("photo");
  const [flashMode, setFlashMode] = useState("off");
  const [showGrid, setShowGrid] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [cameraFullScreen, setCameraFullScreen] = useState(getDefaultCameraFullScreen);
  const [alignmentWarning, setAlignmentWarning] = useState(null);

  const handleClose = useCallback(() => {
    if (isRecording) stopRecording();
    setOpenCamera(false);
    onClose();
    reset();
  }, [onClose, isRecording, stopRecording, reset]);

  const handleFlipCameraWithZoomReset = useCallback(() => {
    handleFlipCamera();
    setZoomLevel(1);
  }, [handleFlipCamera]);

  const zoomSupported = zoomRange != null && zoomRange.max > zoomRange.min;
  const zoomPresets = useMemo(
    () =>
      zoomSupported
        ? ZOOM_PRESETS.filter((p) => p >= zoomRange.min && p <= Math.min(zoomRange.max, 3))
        : [],
    [zoomSupported, zoomRange]
  );
  const handleZoomPreset = useCallback((value) => setZoomLevel(value), []);
  const handleZoomDelta = useCallback(
    (delta) => {
      if (!zoomRange) return;
      setZoomLevel((z) => Math.min(zoomRange.max, Math.max(zoomRange.min, z + delta)));
    },
    [zoomRange]
  );
  const handleFlashCycle = useCallback(() => {
    setFlashMode((prev) => FLASH_MODES[(FLASH_MODES.indexOf(prev) + 1) % FLASH_MODES.length]);
  }, []);

  const triggerCapture = useCallback(() => {
    setShouldCapture(true);
    if (captureTimerRef.current) clearTimeout(captureTimerRef.current);
    captureTimerRef.current = setTimeout(() => {
      setShouldCapture(false);
      captureTimerRef.current = null;
    }, 10);
  }, []);

  const onImageCapture = useCallback(
    async (imageBuffer) => {
      const replaceIndex = replaceAtIndexRef.current;
      try {
        const buffer = await processCapturedBuffer(imageBuffer);
        if (replaceIndex != null && replaceIndex >= 0) {
          replaceAtIndexRef.current = null;
          replaceImageAt(replaceIndex, buffer);
        } else {
          addImage(buffer);
        }
      } catch {
        notifyCaptureFailed();
      }
    },
    [processCapturedBuffer, replaceImageAt, addImage, notifyCaptureFailed]
  );

  const onEditImage = useCallback(
    (imageFile) => updateImageAtEditedIndex(imageFile),
    [updateImageAtEditedIndex]
  );

  const openReupload = useCallback(() => reuploadInputRef.current?.click(), []);

  const handleReuploadFileChange = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || []);
      e.target.value = "";
      if (files.length === 0) return;
      const index = editedIndexRef.current;
      if (index == null || index < 0) return;
      const file = files[0];
      const dataUrl = await processFileForReupload(file);
      if (dataUrl == null) return;
      setImageFiles((prev) => {
        if (index < 0 || index >= prev.length) return prev;
        const next = [...prev];
        const existing = prev[index];
        const hadLabel = typeof existing === "object" && existing != null && existing.label != null;
        next[index] = hadLabel ? { image: dataUrl, label: existing.label } : dataUrl;
        return next;
      });
      setEditedImage({ image: dataUrl, index });
    },
    [processFileForReupload, setImageFiles, setEditedImage]
  );

  const handleFileSelected = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || []);
      const base64Files = await processFilesForGallery(files);
      setImageFiles((prev) => [...base64Files, ...prev]);
      setStackExpanded(false);
    },
    [processFilesForGallery, setImageFiles, setStackExpanded]
  );

  const handleFileInputChange = useCallback(
    (e) => {
      const selectedFiles = Array.from(e.target.files || []);
      if (!validateGalleryFiles(selectedFiles, imageFiles.length)) {
        e.target.value = "";
        return;
      }
      handleFileSelected(e);
      e.target.value = "";
    },
    [validateGalleryFiles, imageFiles.length, handleFileSelected]
  );

  const handleSelectConfirm = useCallback(() => {
    const imageFileList = base64ArrayToFileArray(imageFiles);
    const allFiles =
      imageFileList.length > 0 || videoFiles.length > 0 ? [...imageFileList, ...videoFiles] : [];
    onSelect(allFiles);
  }, [imageFiles, videoFiles, onSelect]);

  const openGallery = useCallback(() => fileInputRef.current?.click(), []);

  const handleGalleryClick = useCallback(
    (e) => {
      const canAdd =
        (multiple || imageFiles.length === 0) &&
        (maxSelect == null || imageFiles.length < maxSelect);
      if (canAdd) openGallery(e);
    },
    [multiple, maxSelect, imageFiles.length, openGallery]
  );

  useEffect(() => setOpenCamera(open), [open]);
  useEffect(() => {
    return () => {
      if (captureTimerRef.current) clearTimeout(captureTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!centerBoxOverlay || !openCamera) {
      setAlignmentWarning(null);
      return;
    }
    const ALIGN_CHECK_MS = 550;
    const msg = "Straighten the device — keep text horizontal (0° or 90°) for best results.";
    const tick = () => {
      const node = videoRef.current;
      const videoEl = node?.video ?? node;
      if (!videoEl) return;
      const result = estimateDominantAngleFromVideo(
        videoEl,
        centerBoxWidthRatio,
        centerBoxHeightRatio
      );
      if (result == null) {
        setAlignmentWarning(null);
        return;
      }
      if (!isAngleAlignedForOcr(result.angle)) {
        setAlignmentWarning(msg);
      } else {
        setAlignmentWarning(null);
      }
    };
    tick();
    const id = setInterval(tick, ALIGN_CHECK_MS);
    return () => {
      clearInterval(id);
      setAlignmentWarning(null);
    };
  }, [centerBoxOverlay, openCamera, centerBoxWidthRatio, centerBoxHeightRatio]);

  const overlayDisplayStyle = useMemo(
    () => ({
      ...OVERLAY_STYLE,
      display: openCamera ? (cameraFullScreen ? "block" : "flex") : "none",
      flexDirection: cameraFullScreen ? undefined : "column",
    }),
    [openCamera, cameraFullScreen]
  );
  const headerStyle = cameraFullScreen ? HEADER_STYLE_FULLSCREEN : HEADER_STYLE_FRAMED;
  const footerStyle = cameraFullScreen
    ? { ...FOOTER_STYLE_FULLSCREEN, ...CONTROLS_STYLE_BASE }
    : { ...FOOTER_STYLE_FRAMED, ...CONTROLS_STYLE_BASE };
  const cameraContainerStyle = useMemo(
    () =>
      cameraFullScreen
        ? { position: "absolute", inset: 0, height: "100dvh", width: "100dvw" }
        : {
            flex: 1,
            minHeight: 0,
            position: "relative",
            width: "100%",
            left: 0,
            right: 0,
            overflow: "hidden",
          },
    [cameraFullScreen]
  );
  const rightStripBottom = cameraFullScreen
    ? "calc(14dvh + 90px)"
    : `calc(${FOOTER_HEIGHT} + 90px)`;
  const stackStripBottom = cameraFullScreen ? "14dvh" : 0;

  const isNextDisabled = imageFiles.length <= 0 && videoFiles.length <= 0;
  const canTakePhoto =
    (multiple || imageFiles.length === 0) && (maxSelect == null || imageFiles.length < maxSelect);
  const isPhotoMode = captureMode === "photo";

  const stackOverlayProps = useMemo(
    () => ({
      imageFiles,
      onThumbnailSelect: setEditedImageFromIndex,
      onRemoveImage: removeImage,
      onCloseStack: closeStack,
      stackClosing,
      onAnimationEnd: handleStackAnimationEnd,
      stripBottom: stackStripBottom,
    }),
    [
      imageFiles,
      setEditedImageFromIndex,
      removeImage,
      closeStack,
      stackClosing,
      handleStackAnimationEnd,
      stackStripBottom,
    ]
  );

  return (
    <>
      <style>{STACK_STRIP_CSS}</style>
      <ImageEditor
        onCancel={handleEditorCancel}
        image={editedImage?.image}
        onEdit={onEditImage}
        onReupload={editedImage != null ? openReupload : undefined}
        onRecapture={editedImage != null ? startRecapture : undefined}
      />
      <div style={overlayDisplayStyle} className="bg-black">
        <div style={headerStyle} className="flex flex-nowrap items-center">
          <button
            type="button"
            onClick={handleClose}
            className="flex-shrink-0 flex items-center justify-center text-white hover:bg-white/15 active:bg-white/20 rounded-full transition-colors w-11 h-11"
            style={{ minWidth: TOUCH_TARGET_MIN, minHeight: TOUCH_TARGET_MIN }}
            aria-label="Close"
          >
            <svg
              className="w-6 h-6 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex-1 min-w-0 flex items-center justify-center px-2 overflow-hidden">
            {title != null && title !== "" ? (
              <span className="block w-full text-white font-medium text-base sm:text-lg text-center truncate">
                {title}
              </span>
            ) : (
              <span className="flex-1 min-w-0" aria-hidden />
            )}
          </div>
          <button
            type="button"
            className="flex-shrink-0 rounded-full font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] px-4 py-2.5 text-sm sm:text-base"
            style={{
              minWidth: TOUCH_TARGET_MIN,
              color: isNextDisabled ? "rgba(255,255,255,0.12)" : "white",
            }}
            onClick={handleSelectConfirm}
            disabled={isNextDisabled}
            aria-label="Confirm and use"
          >
            Done
          </button>
        </div>

        {typeof note === "function" && (
          <div
            className="absolute left-0 right-0 flex justify-center z-10 text-white/80 text-xs sm:text-sm px-4"
            style={{
              top: "calc(56px + env(safe-area-inset-top))",
              paddingLeft: "max(16px, env(safe-area-inset-left))",
              paddingRight: "max(16px, env(safe-area-inset-right))",
            }}
          >
            {note({ maxSelect, fileNumber: imageFiles.length })}
          </div>
        )}
        <div style={cameraContainerStyle} id="camera-container" className="relative">
          {centerBoxOverlay && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10"
              aria-hidden
            >
              <div
                className="border-2 border-white rounded-lg bg-black/20 shadow-lg"
                style={{
                  width: `${Math.max(10, Math.min(100, centerBoxWidthRatio * 100))}%`,
                  height: `${Math.max(10, Math.min(100, centerBoxHeightRatio * 100))}%`,
                }}
              />
              <p
                className="mt-3 text-center text-white text-sm font-medium drop-shadow-md px-4 max-w-[90%]"
                style={{ textShadow: "0 1px 2px rgba(0,0,0,0.8)" }}
              >
                {centerBoxHint ||
                  "Align the reading in the center frame. Keep it straight and readable."}
              </p>
              {alignmentWarning && (
                <p
                  className="mt-2 text-center text-amber-300 text-sm font-semibold drop-shadow-md px-4 max-w-[90%] animate-pulse"
                  style={{ textShadow: "0 1px 2px rgba(0,0,0,0.9)" }}
                  role="alert"
                >
                  ⚠️ {alignmentWarning}
                </p>
              )}
            </div>
          )}
          <CameraPreview
            key={
              useFacingOnMobile
                ? `face:${facingMode}`
                : hasMultipleVideoIds
                  ? `id:${selectedDevice ?? ""}`
                  : `face:${facingMode}`
            }
            ref={videoRef}
            deviceId={
              useFacingOnMobile
                ? undefined
                : hasMultipleVideoIds
                  ? (selectedDevice ?? undefined)
                  : undefined
            }
            facingMode={facingMode}
            onStreamReady={refreshDevices}
            isOn={openCamera}
            shouldCapture={shouldCapture}
            onCapture={onImageCapture}
            showGrid={showGrid}
            showFocusIndicator
            flashOn={flashMode === "on"}
            onTorchSupportChange={setFlashSupported}
            zoomLevel={zoomLevel}
            onZoomSupportChange={setZoomRange}
            onZoomDelta={zoomSupported ? handleZoomDelta : undefined}
            fillHeight={!cameraFullScreen}
          />
          {!cameraFullScreen && (stackExpanded || stackClosing) && imageFiles.length > 0 && (
            <StackStripOverlay {...stackOverlayProps} stripMaxHeight="100%" />
          )}
        </div>

        <div
          className="absolute right-3 flex flex-col items-center gap-4 z-20"
          style={{
            bottom: rightStripBottom,
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          <button
            type="button"
            onClick={() => setCameraFullScreen((f) => !f)}
            className={`w-11 h-11 flex items-center justify-center rounded-full transition-colors bg-black/40 shadow-md ${
              cameraFullScreen ? "text-amber-400 bg-amber-400/25" : "text-white hover:bg-black/55"
            }`}
            aria-label={cameraFullScreen ? "Show frame" : "Full screen"}
            title={cameraFullScreen ? "Show frame" : "Full screen"}
          >
            {cameraFullScreen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6v12h12V6"
                />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={handleFlashCycle}
            className={`w-11 h-11 flex items-center justify-center rounded-full transition-colors bg-black/40 shadow-md ${
              !flashSupported
                ? "text-white/50 cursor-default"
                : flashMode === "on"
                  ? "text-amber-400 bg-amber-400/25"
                  : "text-white hover:bg-black/55"
            }`}
            aria-label={`Flash ${flashMode}${!flashSupported ? " (not available)" : ""}`}
            title={flashSupported ? `Flash: ${flashMode}` : "Flash not available on this camera"}
          >
            <svg
              className="w-6 h-6"
              fill={flashMode === "on" && flashSupported ? "currentColor" : "none"}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => setShowGrid((g) => !g)}
            className={`w-11 h-11 flex items-center justify-center rounded-full transition-colors bg-black/40 shadow-md ${
              showGrid ? "text-amber-400 bg-amber-400/25" : "text-white hover:bg-black/55"
            }`}
            aria-label={showGrid ? "Hide grid" : "Show grid"}
            title={showGrid ? "Grid on" : "Grid off"}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 14a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 14a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"
              />
            </svg>
          </button>
          {allowVideo && (
            <button
              type="button"
              onClick={() => setCaptureMode((m) => (m === "photo" ? "video" : "photo"))}
              className={`w-11 h-11 flex items-center justify-center rounded-full transition-colors bg-black/40 shadow-md ${
                !isPhotoMode ? "text-amber-400 bg-amber-400/25" : "text-white hover:bg-black/55"
              }`}
              aria-label={
                !isPhotoMode ? "Video mode on (tap for photo)" : "Photo mode (tap for video)"
              }
              title={!isPhotoMode ? "Video mode" : "Switch to video"}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
          )}
          {zoomSupported && zoomPresets.length > 0 && (
            <div className="flex flex-col items-stretch rounded-full bg-black/40 shadow-md p-1.5 gap-1 min-w-[1.95rem]">
              {zoomPresets.map((preset) => {
                const closestPreset = zoomPresets.reduce((a, b) =>
                  Math.abs(zoomLevel - a) <= Math.abs(zoomLevel - b) ? a : b
                );
                const isSelected = preset === closestPreset;
                return (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => handleZoomPreset(preset)}
                    className={`min-w-[1.75rem] py-1 px-2 rounded-full text-sm font-semibold transition-colors ${
                      isSelected ? "bg-amber-400 text-black" : "text-white hover:bg-white/10"
                    }`}
                    aria-label={`Zoom ${preset}x`}
                    aria-pressed={isSelected}
                  >
                    {preset}×
                  </button>
                );
              })}
            </div>
          )}
          <button
            type="button"
            onClick={handleFlipCameraWithZoomReset}
            className="w-11 h-11 flex items-center justify-center rounded-full transition-colors bg-black/40 shadow-md text-white hover:bg-black/55"
            aria-label="Flip camera"
            title="Flip camera"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>

        {cameraFullScreen && (stackExpanded || stackClosing) && imageFiles.length > 0 && (
          <StackStripOverlay {...stackOverlayProps} stripMaxHeight="calc(100dvh - 14dvh)" />
        )}

        {videoFiles.length > 0 && (
          <div
            className="absolute left-0 right-0 flex items-center gap-2 overflow-x-auto invisible-scrollbar px-3 py-2 bg-black/60"
            style={{
              bottom: cameraFullScreen ? "calc(14dvh + 70px)" : `calc(${FOOTER_HEIGHT} + 60px)`,
              maxHeight: "100px",
              paddingBottom: "max(8px, env(safe-area-inset-bottom))",
            }}
            role="list"
            aria-label="Recorded videos"
          >
            {videoFiles.map((file, index) => (
              <VideoPreviewItem
                key={`${file.name}-${index}`}
                file={file}
                index={index}
                onRemove={removeVideo}
              />
            ))}
          </div>
        )}

        <div
          id="camera-controllers-container"
          style={footerStyle}
          className="flex items-center justify-center"
        >
          <div
            className="flex flex-col items-center justify-center flex-shrink-0 "
            style={{
              width: "50px",
              minWidth: "50px",
              height: "50px",
              marginBottom: "7px",
            }}
          >
            {imageFiles.length > 0 ? (
              <button
                type="button"
                onClick={() => (stackExpanded ? closeStack() : setStackExpanded(true))}
                className="flex flex-col items-center justify-center rounded-lg active:opacity-80 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-black w-full h-full "
                aria-expanded={stackExpanded}
                aria-label={
                  stackExpanded
                    ? `${imageFiles.length} pages captured, tap to close stack`
                    : `${imageFiles.length} pages captured, tap to open stack`
                }
              >
                <div className="relative flex justify-center items-center w-full h-full ">
                  {imageFiles.slice(0, Math.min(3, imageFiles.length)).map((item, i) => (
                    <div
                      key={i}
                      className="absolute rounded overflow-hidden bg-neutral-900 border border-white/20 shadow-md"
                      style={{
                        width:
                          imageFiles.length === 1 ? "50px" : Math.round(FOOTER_ICON_SIZE * (3 / 4)),
                        height: "50px",
                        left: i * 4,
                        top: i * 2,
                        zIndex: i + 1,
                      }}
                    >
                      {typeof item === "string" &&
                      !item.includes(".pdf") &&
                      !item.startsWith("data:application/pdf") ? (
                        <img src={item} className="w-full h-full object-cover" alt="" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/80 text-[8px] font-semibold">
                          PDF
                        </div>
                      )}
                    </div>
                  ))}
                  {imageFiles.length > 1 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-amber-500 text-black text-[9px] font-bold flex items-center justify-center z-[4] shadow"
                      aria-hidden
                    >
                      {imageFiles.length}
                    </span>
                  )}
                </div>
              </button>
            ) : (
              <div
                className="w-full h-full  rounded-lg border border-white/25 bg-black/50 flex items-center justify-center"
                aria-hidden
              >
                <svg
                  className="w-6 h-6 text-white/50"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            )}
          </div>

          <div className="flex flex-col items-center justify-center gap-1">
            {!isPhotoMode && isRecording && (
              <div className="flex items-center justify-center gap-2 min-h-[24px]">
                <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded">
                  REC
                </span>
                <span className="text-white font-mono text-sm tabular-nums">
                  {formatRecordingTime(recordingElapsedMs)}
                </span>
              </div>
            )}
            {isPhotoMode ? (
              <button
                type="button"
                className="flex items-center justify-center rounded-full hover:scale-105 focus:outline-none focus:ring-4 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-transform"
                style={{
                  width: 72,
                  height: 72,
                  border: "4px solid rgba(255,255,255,0.9)",
                  background: "transparent",
                }}
                disabled={!canTakePhoto}
                onClick={triggerCapture}
                aria-label="Take photo"
              >
                <span
                  className="rounded-full bg-white block"
                  style={{ width: 56, height: 56, opacity: canTakePhoto ? 1 : 0.6 }}
                />
              </button>
            ) : (
              <button
                type="button"
                className="flex items-center justify-center rounded-full hover:scale-105 focus:outline-none focus:ring-4 focus:ring-red-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition-transform"
                style={{
                  width: 72,
                  height: 72,
                  border: "4px solid rgba(255,255,255,0.9)",
                  background: isRecording ? "#dc2626" : "transparent",
                }}
                onClick={isRecording ? stopRecording : startRecording}
                aria-label={isRecording ? "Stop recording" : "Start recording"}
              >
                <span
                  className="block rounded-full bg-red-600 border-2 border-white"
                  style={{
                    width: isRecording ? 24 : 56,
                    height: 56,
                    borderRadius: isRecording ? 4 : "50%",
                  }}
                />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={handleGalleryClick}
            disabled={!allowedFromGallery}
            className={!allowedFromGallery ? "opacity-50 cursor-not-allowed" : ""}
            aria-label="Select from gallery"
          >
            <GalleryIcon size={"50"} />
          </button>
        </div>

        <input
          type="file"
          accept={acceptAttr}
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          multiple={multiple}
        />
        <input
          type="file"
          accept={acceptAttr}
          className="hidden"
          ref={reuploadInputRef}
          onChange={handleReuploadFileChange}
          multiple={false}
        />
      </div>
    </>
  );
}

CameraCapture.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSelect: PropTypes.func,
  inputFileType: PropTypes.string,
  multiple: PropTypes.bool,
  maxSelect: PropTypes.number,
  title: PropTypes.string,
  note: PropTypes.func,
  maxFileSize: PropTypes.number,
  allowVideo: PropTypes.bool,
  /** true = enable gallery button; false/undefined = disable gallery button */
  allowedFromGallery: PropTypes.bool,
  /** true = show center guide box for OCR (align content in frame) */
  centerBoxOverlay: PropTypes.bool,
  /** instruction text shown below the center box (e.g. "Align reading in frame. Keep straight.") */
  centerBoxHint: PropTypes.string,
  centerBoxWidthRatio: PropTypes.number,
  centerBoxHeightRatio: PropTypes.number,
  notificationService: PropTypes.shape({
    warn: PropTypes.func,
    error: PropTypes.func,
  }),
};

CameraCapture.defaultProps = {
  open: false,
  onClose: () => {},
  onSelect: () => {},
  inputFileType: "png,jpeg,jpg",
  multiple: false,
  maxSelect: null,
  title: undefined,
  note: () => null,
  maxFileSize: 50,
  allowVideo: ALLOW_VIDEO_CAPTURE,
  allowedFromGallery: undefined,
  centerBoxOverlay: undefined,
  centerBoxHint: undefined,
  centerBoxWidthRatio: 0.7,
  centerBoxHeightRatio: 0.5,
  notificationService: undefined,
};

export default memo(CameraCapture);
